import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import HomePage from "@/pages/HomePage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminDashboard from "@/pages/AdminDashboard";
import CriteriosDgtPage from "@/pages/CriteriosDgtPage";
import SectionPage from "@/pages/SectionPage";
import NotFoundPage from "@/pages/NotFoundPage";
import UpcomingCoursesPage from "@/pages/UpcomingCoursesPage";
import RecuperacionPuntosPage from "@/pages/RecuperacionPuntosPage";
import ReservaPuntosPage from "@/pages/ReservaPuntosPage";
import PuntosHubPage from "@/pages/PuntosHubPage";
import TeoricaIntensivaPage from "@/pages/TeoricaIntensivaPage";
import CambiosDGTOctubre2026 from "@/pages/blog/CambiosDGTOctubre2026";
import CursoSensibilizacionPage from "@/pages/CursoSensibilizacionPage";
import CicloAdicionalPage from "@/pages/CicloAdicionalPage";
import CenterPage from "@/pages/CenterPage";
import PermitLandingPage from "@/pages/PermitLandingPage";
import PistasExamenPage from "@/pages/PistasExamenPage";
import { PERMIT_LANDING_SLUGS } from "@/data/permitLandings";
import Permits from "@/components/Permits";
import CapSection from "@/components/CapSection";
import PuntosSection from "@/components/PuntosSection";
import TheoryTest from "@/components/TheoryTest";
import Centers from "@/components/Centers";
import { JsonLd } from "@/lib/seo";
import { SEDES_SCHEMA } from "@/lib/sedesSchema";
import Reviews from "@/components/Reviews";
import { AuthProvider } from "@/context/AuthContext";
import useLenis from "@/hooks/useLenis";
import axios from "axios";
import { API } from "@/lib/faro";
import { initGA, trackPageView } from "@/lib/analytics";
import LegalPage from "@/pages/LegalPage";
import { CookieBanner, getCookieConsent } from "@/components/CookieBanner";

function RedirectWithHash({ to }) {
  const { hash } = useLocation();
  return <Navigate to={`${to}${hash || ""}`} replace />;
}

function AnalyticsBootstrap() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const init = () => {
      axios.get(`${API}/config`).then(({ data }) => {
        if (data?.ga_measurement_id) initGA(data.ga_measurement_id);
      }).catch(() => {});
    };
    if (getCookieConsent() === "accepted") init();
    window.addEventListener("faro-consent-accepted", init);
    return () => window.removeEventListener("faro-consent-accepted", init);
  }, []);
  useEffect(() => {
    trackPageView(pathname + (hash || ""));
  }, [pathname, hash]);
  return null;
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      let attempts = 0;
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          [1200, 2200, 3200].forEach((ms) =>
            setTimeout(() => {
              const r = el.getBoundingClientRect();
              if (Math.abs(r.top) > 80) el.scrollIntoView({ behavior: "auto", block: "start" });
            }, ms)
          );
          return;
        }
        if (++attempts < 25) setTimeout(tryScroll, 100);
      };
      setTimeout(tryScroll, 60);
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    // Lenis keeps its own scroll state: force it to the top too or it restores the old position
    const toTop = () => {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
      window.scrollTo(0, 0);
    };
    toTop();
    const t = setTimeout(toTop, 150);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useLenis();
  return null;
}

function TrailingSlashRedirect() {
  const { pathname, search, hash } = useLocation();
  if (pathname !== "/" && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") + search + hash} replace />;
  }
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll />
          <TrailingSlashRedirect />
          <ScrollManager />
          <AnalyticsBootstrap />
          <CookieBanner />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/recuperacion-de-puntos-vigo" element={<RecuperacionPuntosPage />} />
            <Route path="/reserva-puntos" element={<ReservaPuntosPage />} />
            <Route path="/permiso-por-puntos-vigo" element={<PuntosHubPage />} />
            <Route path="/curso-sensibilizacion-vigo" element={<CursoSensibilizacionPage />} />
            <Route path="/curso-intensivo-teorica-vigo" element={<TeoricaIntensivaPage />} />
            <Route path="/ciclo-adicional-vigo" element={<CicloAdicionalPage />} />
            {PERMIT_LANDING_SLUGS.map((s) => (
              <Route key={s} path={`/${s}`} element={<PermitLandingPage slug={s} />} />
            ))}
            <Route path="/pistas-examen-dgt-vigo" element={<PistasExamenPage />} />
            {["autoescuela-plaza-america", "autoescuela-coruxo", "autoescuela-panxon", "autoescuela-nigran", "autoescuela-a-caniza"].map((s) => (
              <Route key={s} path={`/${s}`} element={<CenterPage />} />
            ))}
            <Route path="/permisos" element={<SectionPage title="Permisos y carnets" seoTitle="Permisos y Carnets de Conducir en Vigo | Autoescuelas Faro" seoDescription="Todos los permisos: AM, A1, A2, A, B, B96, C, C+E, D y CAP. 5 centros en Vigo, Nigrán y A Cañiza con pistas propias donde te examina la DGT." canonical="https://autoescuelasfaro.com/permisos"><Permits /></SectionPage>} />
      <Route path="/cap" element={<RedirectWithHash to="/curso-cap-vigo" />} />
            <Route path="/puntos" element={<RedirectWithHash to="/recuperacion-de-puntos-vigo" />} />
            <Route path="/aviso-legal" element={<LegalPage slug="aviso-legal" />} />
            <Route path="/politica-privacidad" element={<LegalPage slug="politica-privacidad" />} />
            <Route path="/politica-cookies" element={<LegalPage slug="politica-cookies" />} />
            <Route path="/examen" element={<SectionPage title="Examen DGT" seoTitle="Test Teórico DGT Gratis Online | Autoescuelas Faro Vigo" seoDescription="Practica gratis el examen teórico de la DGT con preguntas tipo test reales y corrección al instante. Preparado por los profesores de Autoescuelas Faro en Vigo." canonical="https://autoescuelasfaro.com/examen"><TheoryTest /></SectionPage>} />
            <Route path="/centros" element={<SectionPage title="Centros" seoTitle="Nuestros Centros en Vigo, Nigrán y A Cañiza | Autoescuelas Faro" seoDescription="5 autoescuelas: Plaza América, Coruxo, Panxón, Nigrán y A Cañiza. Horarios, teléfonos, WhatsApp y cómo llegar a cada centro." canonical="https://autoescuelasfaro.com/centros"><JsonLd data={SEDES_SCHEMA} /><Centers /></SectionPage>} />
            <Route path="/opiniones" element={<SectionPage title="Opiniones" seoTitle="Opiniones de Alumnos | Autoescuelas Faro Vigo" seoDescription="Opiniones reales de nuestros alumnos: 4,6 estrellas en Google con 148 reseñas. Formando conductores en Vigo desde 1980." canonical="https://autoescuelasfaro.com/opiniones"><Reviews /></SectionPage>} />
            <Route path="/proximos-cursos" element={<UpcomingCoursesPage />} />
            <Route path="/criterios-dgt" element={<CriteriosDgtPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/cambios-dgt-1-octubre-2026" element={<CambiosDGTOctubre2026 />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
